import type { IAddCancellationCmd, IAddCancellationPositionData } from "@/data/commands/order/AddCancellationCmd";
import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { CancellationMode, CancellationPositionType, RefundMode, RefundabilityType } from "@/data/model/Enums";
import type { IFullOrder, IOrderItem } from "@/data/model/Order";
import { Format } from "@/data/util/Format";
import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";



import { BaseModal } from "@/shared/elements/BaseModal";
import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";



import { NumberInput } from "@/portal/formControls/NumberInput";
import { Switch } from "@/portal/formControls/Switch";
import { showSelectCancellationReasonModal } from "@/portal/modal/CancellationWindowUtils";
import { UiNotification } from "@/portal/ui/Notification";


export class CancellationWindow extends BaseModal {
    private readonly $form: JQuery;
    private $submitBtn: JQuery;

    private amountInput?: NumberInput;

    private $positions?: JQuery;

    private $refundContainer: JQuery;
    private refundToggle: Switch;
    private $refundToggleContainer: JQuery;

    private refundModeToggle: Switch;
    private $refundModeToggleContainer: JQuery;

    private mode: CancellationMode;
    private orderId: string;
    private isAdmin: boolean;
    private doNotShowRefundToggles: boolean;

    private order?: IFullOrder;

    // Revenue effective max cancellable amount
    private maxCancellableAmount?: number;
    private cancellableAmount?: number; //get updated on button validation

    private dao: OrderDao;

    private onOrderCancellationSucceeded: () => void;
    private onOrderCancellationFailed: (errorCode: string) => void;

    constructor(
        orderId: string,
        isAdmin: boolean,
        doNotShowRefundToggles: boolean,
        mode: CancellationMode,
        onOrderCancellationSucceeded: () => void,
        onOrderCancellationFailed: (errorCode: string) => void
    ) {
        super(
            `${translations.cancellation.cancellation} - ${translations.enums.cancellationMode[CancellationMode[mode]]}`,
            "modal-lg",
            true
        );

        this.isAdmin = isAdmin;
        this.doNotShowRefundToggles = doNotShowRefundToggles;
        this.onOrderCancellationSucceeded = onOrderCancellationSucceeded;
        this.onOrderCancellationFailed = onOrderCancellationFailed;

        this.$element.addClass("cancellation");
        this.orderId = orderId;
        this.mode = mode;
        this.dao = Factory.getInstance<OrderDao>(OrderDao);

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        this.$form.append(`<div class="centered">${translations.cancellation.loading}</div>`);

        this.loadOrderData(mode);

        this.$refundContainer = $('<div class="refund-container"></div>').appendTo(this.$footer!);

        this.$refundModeToggleContainer = $('<div class="refund-toggle-container mr-auto" />')
            .appendTo(this.$refundContainer)
            .hide();

        this.refundModeToggle = new Switch(
            $('<input data-switch="true" type="checkbox" name="refundmode" />').appendTo(
                this.$refundModeToggleContainer
            )
        ).renderTo(this.$refundModeToggleContainer);
        this.$refundModeToggleContainer.append(
            `<label class="form-label">${translations.cancellation.offlineMode}</label>`
        );

        this.$refundToggleContainer = $('<div class="refund-toggle-container mr-auto" />')
            .appendTo(this.$refundContainer)
            .hide();

        this.refundToggle = new Switch(
            $('<input data-switch="true" type="checkbox" name="refund" />').appendTo(this.$refundToggleContainer)
        ).renderTo(this.$refundToggleContainer);

        this.$refundToggleContainer.append(`<label class="form-label">${translations.cancellation.refund}</label>`);

        $('<button type="button" class="btn btn-secondary">' + translations.cancellation.notNow + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-danger">' + translations.cancellation.submitBtn + "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();

            return false;
        });
    }

    private async loadOrderData(mode: CancellationMode) {
        try {
            // fetch order data
            this.order = await this.dao.getSingle(this.orderId, { showLocationOrderItemsIfLocationIsNotOwner: true });

            // calculate maximum cancellable amount
            this.maxCancellableAmount = this.order!.totalRemainingPaidAmount;

            // clear form
            this.$form.empty();

            if (this.showRefundModeToggle()) {
                this.$refundModeToggleContainer.show();
            } else {
                this.$refundModeToggleContainer.hide();
            }

            if (this.showRefundToggle()) {
                this.$refundToggleContainer.show();
            } else {
                this.$refundToggleContainer.hide();
            }

            // set up form based on the mode
            switch (mode) {
                case CancellationMode.WholeOrder:
                    this.setWholeOrder();
                    break;
                case CancellationMode.Amount:
                    this.setAmount();
                    break;
                case CancellationMode.Positions:
                    this.setPositions();
                    break;
            }
        } catch (ex) {
            logError(ex);
            UiNotification.showException(translations.cancellation.failedLoading);
            this.close();
        }
    }

    private showRefundModeToggle(): boolean {
        return (
            !this.doNotShowRefundToggles &&
            this.isAdmin &&
            this.order?.refundabilityType === RefundabilityType.Refundable
        );
    }

    private showRefundToggle(): boolean {
        return this.order?.refundabilityType === RefundabilityType.RefundOptionalForOperators;
    }

    private setWholeOrder() {
        this.$form.append(`<div class="text centered">${translations.cancellation.wholeOrderDesc}</div>`);
        this.checkButtonState();
    }

    private setPositions() {
        // add description
        this.$form.append(`<div class="text">${translations.cancellation.positionsText}</div>`);

        // create a container for the positions
        this.$positions = $('<div class="positions scrollable" />').appendTo(this.$form);

        // render order items
        this.renderOrderItems();

        // render tip position, if any
        this.renderTipPosition();

        // check button state and adjust the layout
        this.checkButtonState();
        this.onResize();
    }

    private renderOrderItems() {
        if (!this.order) return;

        for (const item of this.order.items) {
            const position = this.renderCancellableItemPosition(item);
            if (position) {
                position.appendTo(this.$positions!);
            }
        }
    }

    private renderTipPosition() {
        if (!this.order || this.order.tip === 0) return;

        const tipPosition = this.renderCancellableTipPosition(this.order.tip);
        if (tipPosition) {
            tipPosition.appendTo(this.$positions!);
        }
    }

    private setAmount() {
        // create form group for the amount input
        const formGroup = this.createAmountFormGroup();

        // initialize NumberInput
        this.amountInput = new NumberInput(formGroup.find("> .col-xl-6 > input"), {
            min: 0,
            max: this.maxCancellableAmount,
            step: 0.01,
            decimals: 2,
        });
        this.amountInput.onChange = () => this.checkButtonState();

        // initial button state check
        this.checkButtonState();
    }

    private createAmountFormGroup(): JQuery {
        const amountLabel = translations.cancellation.amount;
        const formattedAmount = `CHF ${this.maxCancellableAmount!.toFixed(2)}`;

        const formGroupHtml = `
            <div class="form-group row">
                <label class="col-xl-4 col-form-label">
                    ${amountLabel} <span class="mandatory">*</span>
                </label>
                <div class="col-xl-6">
                    <input type="text" name="amount" class="form-control" placeholder="${formattedAmount}" />
                </div>
            </div>`;

        return $(formGroupHtml).appendTo(this.$form);
    }

    protected onShow() {
        $(window).bind("resize.cancellation", () => this.onResize());
        this.onResize();
    }

    protected onHide() {
        $(window).unbind("resize.cancellation");

        this.amountInput?.destroy();
        if (this.$positions !== undefined) {
            this.$positions.find("> .position").each((i, e) => {
                $(e).data("qtyctrl")?.destroy();
            });
        }

        this.$element.remove();
    }

    private onSubmit() {
        const data: IAddCancellationCmd = {
            mode: this.mode,
            refundMode: RefundMode.Online,
            isRefundApprovedByOperator: false,
        };

        if (this.showRefundToggle()) {
            data.isRefundApprovedByOperator = this.refundToggle.getValue()!;
        } else if (this.showRefundModeToggle()) {
            data.refundMode = this.refundModeToggle.getValue() ? RefundMode.Offline : RefundMode.Online;
        }

        //validate modes
        let hasError = false;
        this.$form.find("is-invalid").removeClass("is-invalid");
        switch (data.mode) {
            case CancellationMode.WholeOrder:
                if (this.order?.cancellations!.length !== 0) {
                    hasError = true;
                }

                break;
            case CancellationMode.Amount:
                this.amountInput!.setError(true);
                hasError = true;
                break;
            case CancellationMode.Positions:
                data.positions = new Array<IAddCancellationPositionData>();

                this.$positions!.find("> .position").each((i, e) => {
                    const qtyInput: NumberInput = $(e).data("qtyctrl");
                    const amt = qtyInput.getValue();
                    if (amt! > 0) {
                        if ($(e).hasClass("tip")) {
                            data.positions!.push({
                                billId: undefined,
                                positionType: CancellationPositionType.Tip,
                                quantity: 1,
                            });
                        } else {
                            data.positions!.push({
                                billId: undefined,
                                positionType: CancellationPositionType.OrderItem,
                                orderItemId: $(e).data("itemId"),
                                quantity: amt!,
                            });
                        }
                    }
                });

                if (data.positions.length === 0) hasError = true;

                break;
        }

        if (hasError) return;

        const cancellationModalOptions = {
            locationId: this.order!.locationId,
            orderId: this.orderId,
            cancellableAmount: this.cancellableAmount!,
            data: data,
            onSaveStart: () => {
                this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true);
            },
            onSaveError: (errorCode: string, errorMessage: string) => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                UiNotification.showException(errorMessage, undefined, () => {
                    this.onOrderCancellationFailed(errorCode);
                    this.close();
                });
            },
            onSaveEnd: () => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                this.onOrderCancellationSucceeded();
                this.close();
            },
        };

        // start process of cancellation:
        // if mode is Positions or WholeOrder:
        // 1. select cancellation reason
        // 2. confirm if stock should be changed (if applicable)
        // 3. confirm if you are sure
        // 4. add cancellation
        showSelectCancellationReasonModal(cancellationModalOptions);
    }

    private renderCancellableItemPosition(item: IOrderItem): JQuery | undefined {
        // if item is already fully cancelled - return
        if (item.currentTotalQuantity == 0) return undefined;

        // create the position container and input field
        const position = $('<div class="position item" />').data("itemId", item.id);
        const quantityContainer = $('<div class="quantity" />').appendTo(position);
        const qtyInputField = $('<input class="form-control" value="0" />').appendTo(quantityContainer);

        // initialize the number input control with the cancellable quantity as the max value
        const qtyInput = new NumberInput(qtyInputField, {
            min: 0,
            max: item.currentTotalQuantity,
            step: 1,
            decimals: 0,
        });

        // update button state when the input value changes
        qtyInput.onChange = () => this.checkButtonState();

        // store the quantity control in the position data for later use
        position.data("qtyctrl", qtyInput);

        // append the max cancellable quantity text to the quantity container
        quantityContainer.append(`<span>${translations.general.of} ${item.currentTotalQuantity}</span>`);

        // render and append the item name to the position container
        this.recursiveRenderItemName(item, false).appendTo(position);

        // calculate and display the item unit price
        const amountContainer = $('<div class="amt" />').appendTo(position);
        amountContainer.append(`CHF ${item.unitDiscountedPriceWithOptions!.toFixed(2)}`);

        return position;
    }

    private renderCancellableTipPosition(tip: number) {
        if (this.order?.isTipCancelled) return undefined;

        const cancellables = 1;
        const position = $('<div class="position tip" />');
        const quantity = $('<div class="quantity" />').appendTo(position);
        const qtyInputField = $('<input class="form-control" value="0" />').appendTo(quantity);

        const qtyInput = new NumberInput(qtyInputField, {
            min: 0,
            max: 1,
            step: cancellables,
            decimals: 0,
        });

        qtyInput.onChange = () => this.checkButtonState();

        position.data("qtyctrl", qtyInput);
        quantity.append(`<span>${translations.general.of} ${cancellables}</span>`);

        const itmC = $('<div class="item" />').appendTo(position);
        itmC.append(`<div class="name">${translations.pay.tip}</div>`);

        const amountC = $('<div class="amt" />').appendTo(position);
        amountC.append("CHF " + tip.toFixed(2));

        return position;
    }

    private recursiveRenderItemName(item: IOrderItem, showQty: boolean): JQuery {
        const itmC = $('<div class="item" />');
        const itmName = getNameInLocationLanguage(item, true);

        itmC.append(
            '<div class="name">' +
                (showQty && item.quantity > 1 ? item.quantity + "x " : "") +
                Format.htmlEscape(itmName) +
                "</div>"
        );

        if (item.options.length !== 0) {
            const optionsC = $('<div class="options" />').appendTo(itmC);

            for (const oi in item.options) {
                const option = item.options[oi];

                const optionC = $('<div class="option" />').appendTo(optionsC);
                if (option.displayName)
                    optionC.append('<div class="name">' + Format.htmlEscape(getNameInLocationLanguage(option, true)) + "</div>");

                if (option.items.length !== 0) {
                    const items = $('<div class="items" />').appendTo(optionC);
                    for (const oai in option.items)
                        this.recursiveRenderItemName(option.items[oai], true).appendTo(items);
                }
            }
        }

        return itmC;
    }

    private checkButtonState() {
        this.cancellableAmount = 0;
        let isEnabled = false;
        switch (this.mode) {
            case CancellationMode.Amount:
                this.cancellableAmount = this.amountInput!.getValue();
                if (this.cancellableAmount === undefined) this.cancellableAmount = 0;

                if (this.cancellableAmount > 0) isEnabled = true;

                break;
            case CancellationMode.WholeOrder:
                this.cancellableAmount = this.maxCancellableAmount;
                isEnabled = true;
                break;
            case CancellationMode.Positions:
                this.$positions!.find("> .position").each((i, e) => {
                    const qtyInput: NumberInput = $(e).data("qtyctrl");
                    const quantity = qtyInput.getValue()!;
                    if (quantity > 0) {
                        if (!isEnabled) isEnabled = true;

                        if ($(e).hasClass("tip")) {
                            this.cancellableAmount! += this.order!.tip;
                        } else {
                            const orderItemId = $(e).data("itemId");
                            const orderItem = this.order!.items.find((c) => c.id === orderItemId);

                            if (orderItem) {
                                this.cancellableAmount! += orderItem.unitDiscountedPriceWithOptions! * quantity;
                            }
                        }
                    }
                });

                break;
        }

        this.$submitBtn.html(`${translations.cancellation.submitBtn} - CHF ${this.cancellableAmount!.toFixed(2)}`);

        if (isEnabled) {
            this.$submitBtn.removeClass("disabled").removeAttr("disabled");
        } else {
            this.$submitBtn.addClass("disabled").attr("disabled", "disabled");
        }
    }

    private onResize() {
        if (this.$positions === undefined) return;

        const windowHeight = $(window).height();
        this.$positions.removeAttr("style");
        const currentHeight = this.$positions.height();
        const maxHeight = windowHeight - 300;
        if (currentHeight > maxHeight) this.$positions.height(maxHeight);
    }
}
